import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { refDataQueryOptions } from '../../../hooks/data/useRefData'
import { samplingAreasQueryOptions } from '../../../hooks/data/useSamplingAreas'
import type { MonitoringSOCSamplingAreaSample } from '../../../types/apiModels'
import {
  renderEditSingleSelect,
  renderInput,
  renderSelect,
  renderText,
  validateInputStringRequired,
  validatePercentageNumber,
  validatePositiveNumberRequired,
  validateSandPercentage,
  validateSingleSelectRequired,
  validateSoilTexturePercentages,
} from '../DataGrid/DataGridControls'

export const samplingAreasColumn = {
  field: 'samplingAreaId',
  headerName: 'Estrato',
  headerAlign: 'center',
  validate: ({ value, _ }) => validateSingleSelectRequired(value, 'Requerido'),
  renderCell: params => renderSelect({ params, props: { tooltip: { placement: 'top' } } }),
  renderEditCell: params =>
    renderEditSingleSelect({ params, props: { tooltip: { placement: 'top' } } }),
  required: true,
  editable: true,
  sortable: true,
  width: 160,
  type: 'singleSelect',
}

export const soilTexturesColumn = {
  field: 'soilTextureTypeId',
  headerName: 'Clase textural',
  headerAlign: 'center',
  validate: ({ value, _ }) => validateSingleSelectRequired(value, 'Requerida'),
  renderCell: params => renderSelect({ params, props: { tooltip: { placement: 'top' } } }),
  renderEditCell: params =>
    renderEditSingleSelect({ params, props: { tooltip: { placement: 'top' } } }),
  required: true,
  editable: true,
  sortable: true,
  width: 200,
  type: 'singleSelect',
  allowOptional: true,
}

export const monitoringSOCSamplingAreasFixedColumns = [
  {
    field: 'stationsName',
    headerName: 'Estaciones',
    headerAlign: 'center',
    validate: ({ value, _ }) => validateInputStringRequired(value, 'Requerido'),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: true,
    width: 170,
  },
  {
    field: 'samplingAreaName',
    headerName: 'Nombre muestra compuesta',
    headerAlign: 'center',
    validate: ({ value, _ }) => validateInputStringRequired(value, 'Requerida'),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: true,
    width: 170,
  },
  {
    field: 'ph',
    headerName: 'pH',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) =>
      validatePositiveNumberRequired({
        value,
        allowZero: true,
        range: { min: 0, max: 14 },
        column,
      }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: false,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'nitrogenPercentage',
    headerName: 'Nitrógeno [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) => validatePercentageNumber({ value, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: false,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'phosphorusPercentage',
    headerName: 'P [ppm]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) => validatePercentageNumber({ value, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: false,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'clayPercentage',
    headerName: 'Arcilla [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, row, column }) => validateSoilTexturePercentages({ value, row, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
    allowOptional: true,
  },
  {
    field: 'fineSandPercentage',
    headerName: 'Arena\nfina [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) => validatePercentageNumber({ value, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
    allowOptional: true,
  },
  {
    field: 'coarseSandPercentage',
    headerName: 'Arena\ngruesa [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) => validatePercentageNumber({ value, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
    allowOptional: true,
  },
  {
    field: 'sandPercentage',
    headerName: 'Arena [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, row, column }) => {
      const soilTextureError = validateSoilTexturePercentages({
        value,
        row,
        column,
      })
      if (soilTextureError) return soilTextureError

      return validateSandPercentage({ value, row, column })
    },
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
    allowOptional: true,
  },
  {
    field: 'siltPercentage',
    headerName: 'Limo [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, row, column }) => validateSoilTexturePercentages({ value, row, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
    allowOptional: true,
  },
]

export const useSOCSamplingAreasColumns = (farmId: string) => {
  const [monitoringSOCSamplingAreaSamplesColumns, setMonitoringSOCSamplingAreaSamplesColumns] =
    useState<any>(null)
  const [monitoringSOCSamplingAreaSamples, setMonitoringSOCSamplingAreaSamples] = useState<
    MonitoringSOCSamplingAreaSample[]
  >([])
  const [optionalColumns, setOptionalColumns] = useState<Record<string, boolean>>({})
  const queryClient = useQueryClient()

  const loadColumns = useCallback(async () => {
    // The request would otherwise go out without the farm filter and answer every farm's
    // strata, offering foreign options in the select and poisoning the shared cache.
    // The empty set has to be published, not just returned: the dialog opens its grid on
    // the form state alone, so an unset column state reaches DataGrid as a null it maps over.
    if (!farmId) {
      setMonitoringSOCSamplingAreaSamplesColumns([])
      return []
    }

    // Raw cached entities — read-only, they are shared with every other strata consumer.
    const samplingAreas = await queryClient.ensureQueryData(samplingAreasQueryOptions(farmId))

    const soilTextures = await queryClient.ensureQueryData(refDataQueryOptions('RefSoilTextures'))

    const columns = [
      {
        ...samplingAreasColumn,
        valueOptions: samplingAreas?.map(samplingArea => {
          return { value: samplingArea.id, label: samplingArea.name }
        }) || [''],
      },
      ...monitoringSOCSamplingAreasFixedColumns,
      {
        ...soilTexturesColumn,
        valueOptions: soilTextures?.map(soilTexture => {
          return { value: soilTexture.id, label: soilTexture?.es_AR_long || '' }
        }) || [''],
      },
    ]

    setMonitoringSOCSamplingAreaSamplesColumns(columns)
    return columns
  }, [farmId, queryClient])

  const initializeOptionalColumns = useCallback((samples: any[], columns: any) => {
    if (samples.length > 0 && columns) {
      const initialOptionalColumns = {}

      const optionalColumnFields = columns
        .filter(column => (column as any).allowOptional)
        .map(column => column.field)

      optionalColumnFields.forEach(field => {
        const hasAnyValue = samples.some(row => {
          const value = row[field]
          return value !== null && value !== undefined && value !== ''
        })

        initialOptionalColumns[field] = !hasAnyValue
      })

      setOptionalColumns(initialOptionalColumns)
    }
  }, [])

  const processRowUpdate = useCallback(
    (newRow: any) => {
      const { errors, ...updateRowWithoutErrors } = newRow

      const oldRow = monitoringSOCSamplingAreaSamples.find(row => row.id === newRow.id)
      const updatedField = Object.keys(newRow).find(
        key =>
          newRow[key] !== oldRow?.[key] &&
          (key === 'fineSandPercentage' || key === 'coarseSandPercentage'),
      )

      const columns = optionalColumns as any
      const shouldAutoCalculateSand = !columns?.fineSandPercentage && !columns?.coarseSandPercentage

      if (updatedField && shouldAutoCalculateSand) {
        const fineSandValue = parseFloat(newRow.fineSandPercentage) || 0
        const coarseSandValue = parseFloat(newRow.coarseSandPercentage) || 0

        if (fineSandValue > 0 && coarseSandValue > 0) {
          const calculatedSandPercentage = fineSandValue + coarseSandValue
          updateRowWithoutErrors.sandPercentage = calculatedSandPercentage
        }
      }

      const updatedRow = { ...updateRowWithoutErrors, id: newRow.id }

      setMonitoringSOCSamplingAreaSamples(oldRows =>
        oldRows.map(row => (row.id === newRow.id ? updatedRow : row)),
      )

      return updatedRow
    },
    [monitoringSOCSamplingAreaSamples, optionalColumns],
  )

  const columns = useMemo(() => {
    if (!monitoringSOCSamplingAreaSamplesColumns) return null

    return monitoringSOCSamplingAreaSamplesColumns.map(column => {
      if (column.field === 'samplingAreaId') {
        const allValueOptions = column.valueOptions || []

        return {
          ...column,
          valueOptions: params => {
            const selectedSamplingAreaIds = monitoringSOCSamplingAreaSamples
              .filter(row => row.id !== params.id && row.samplingAreaId && !row.isDeleted)
              .map(row => row.samplingAreaId)

            return allValueOptions.filter(option => !selectedSamplingAreaIds.includes(option.value))
          },
        }
      }

      if (column.field === 'sandPercentage') {
        const isSandEditable =
          !!optionalColumns?.fineSandPercentage && !!optionalColumns?.coarseSandPercentage

        return {
          ...column,
          editable: isSandEditable,
          renderEditCell: params => {
            return isSandEditable
              ? renderInput({ params, props: { tooltip: { placement: 'bottom' } } })
              : renderText({ params, props: { tooltip: { placement: 'bottom' } } })
          },
        }
      }
      return column
    })
  }, [monitoringSOCSamplingAreaSamplesColumns, monitoringSOCSamplingAreaSamples, optionalColumns])

  return {
    columns,
    samples: monitoringSOCSamplingAreaSamples,
    setSamples: setMonitoringSOCSamplingAreaSamples,
    optionalColumns,
    setOptionalColumns,
    loadColumns,
    initializeOptionalColumns,
    processRowUpdate,
  }
}
